import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Field, Select } from "@aigntx/ui";
import { endpoints } from "../api";
import type { DemoAccount } from "../types";

/**
 * Pick a party, get its credentials filled in.
 *
 * Checking that the Contractor cannot see a variation under review means signing
 * in as the Contractor — and before this, that meant keeping a list of test
 * emails and passwords beside the keyboard. The list belongs in the app.
 *
 * Renders NOTHING unless the API is in demo mode. The endpoint 404s otherwise,
 * so on a real deployment this component is silently absent rather than an
 * empty box hinting at accounts that are not there.
 */
export function TestSignIn({
  slug,
  onPick,
}: {
  slug: string;
  onPick: (email: string, password: string) => void;
}) {
  const { t } = useTranslation();
  const [accounts, setAccounts] = useState<DemoAccount[] | null>(null);
  const [chosen, setChosen] = useState("");

  useEffect(() => {
    let live = true;
    if (!slug) {
      setAccounts([]);
      return;
    }
    endpoints
      .demoAccounts(slug)
      .then((list) => live && setAccounts(list))
      .catch(() => live && setAccounts([])); // no test drive here — say nothing
    return () => {
      live = false;
    };
  }, [slug]);

  if (!accounts || accounts.length === 0) return null;

  const picked = accounts.find((a) => a.role === chosen);

  return (
    <div className="testdrive">
      <div className="testdrive__head">
        <span className="testdrive__tag">{t("demo.tag")}</span>
        <span className="muted">{t("demo.hint")}</span>
      </div>
      <Field label={t("demo.signInAs")} htmlFor="demo-role">
        <Select
          id="demo-role"
          value={chosen}
          onChange={(e) => {
            const role = e.target.value;
            setChosen(role);
            const account = accounts.find((a) => a.role === role);
            if (account) onPick(account.email, account.password);
          }}
        >
          <option value="">{t("demo.choose")}</option>
          {accounts.map((a) => (
            <option key={a.role} value={a.role}>
              {a.roleLabel}
              {a.company ? ` — ${a.company}` : ""}
            </option>
          ))}
        </Select>
      </Field>
      {picked && (
        <p className="testdrive__sees">
          <strong>{picked.partyLabel}</strong> · {picked.sees}
        </p>
      )}
    </div>
  );
}
